import { Entity, Nullable } from '@hglobal/common';

export enum ChatMessageRole {
  User = 'USER',
  Agent = 'AGENT',
}

export enum ChatLanguage {
  En = 'EN',
  Ru = 'RU',
  Uk = 'UK',
}

export interface ChatMessage extends Entity<string> {
  chatId: string;
  role: ChatMessageRole;
  content: string;
  language: ChatLanguage;
  createdAt: string;
  updatedAt: string;
  /** Knowledge-base pages the answer was derived from. */
  sources?: ChatSource[];
  /** Inline reference links extracted from the answer content. */
  links?: ChatLink[];
  /** Front-end only: optimistic send state. */
  internalStatus?: InternalMessageStatus;
  /** Front-end only: current thumbs up/down selection. Not persisted across reloads. */
  reactionType?: ChatMessageReaction;
  /** Front-end only: true once a "Leave a review" form was submitted for this answer. */
  isFeedbackProvided?: boolean;
  /**
   * Front-end only: whether this answer accepts feedback. `false` for client-only
   * replies (static/welcome/placeholder) that have no server-side message id.
   */
  isFeedbackEligible?: boolean;
  /** Front-end only: true while SSE answer tokens are still arriving. */
  isStreaming?: boolean;
  /** Front-end only: optimistic/static row that will never appear in a server history snapshot. */
  isClientOnly?: boolean;
}

/** Confluence/knowledge-base page cited as a source for an answer. */
export interface ChatSource {
  pageTitle: string;
  pageUrl: string;
  spaceKey?: string;
}

/** Reference link returned alongside an answer (also embedded inline in `content`). */
export interface ChatLink {
  label: string;
  url: string;
  normalizedUrl?: string;
  /** Link classification for audit/validation (e.g. 'document'). */
  kind?: string;
  /** Knowledge-base page the link was resolved from. */
  sourcePageId?: string;
}

export interface CreateChatMessageReq {
  /** `null` on the first message — backend creates the chat implicitly. */
  chatId: string | null;
  content: string;
  /** Active context — scopes the new chat to "Personal" or "Work". */
  context?: AiChatContext;
  /** Option from `WORK.workContexts` (without its `label`) for creating a new "Work" chat. */
  workContext?: WorkContextOption;
}

/** SSE `meta` — chat id is known before answer tokens arrive. */
export interface ChatMessageStreamMeta {
  type: 'meta';
  chatId: string;
  phase: 'processing';
}

/** SSE `answer_delta` — append this chunk to the provisional answer. */
export interface ChatMessageStreamAnswerDelta {
  type: 'answer_delta';
  text: string;
}

/** SSE `answer_replace` — replace the provisional answer with this full text. */
export interface ChatMessageStreamAnswerReplace {
  type: 'answer_replace';
  text: string;
}

/** SSE `final` — persisted AGENT message (authoritative). */
export interface ChatMessageStreamFinal {
  type: 'final';
  message: ChatMessage;
}

/** SSE `error` — generation failed; `hadContent` means some deltas already arrived. */
export interface ChatMessageStreamError {
  type: 'error';
  message: string;
  hadContent: boolean;
}

export type ChatMessageStreamEvent =
  | ChatMessageStreamMeta
  | ChatMessageStreamAnswerDelta
  | ChatMessageStreamAnswerReplace
  | ChatMessageStreamFinal
  | ChatMessageStreamError;

export interface RenameChatReq {
  title: string;
}

export interface GenerateChatTitleReq {
  content: string;
}

export interface PinChatReq {
  isPinned: boolean;
}

export interface ChatListItem extends Entity<string> {
  userEmail: string;
  title: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  context?: AiChatContext;
  license?: string;
  /**
   * Work scope of a "Work" chat (e.g. `QUALITY_CONTROL`); at most one of `license` / `workScope` is
   * set.
   */
  workScope?: string | null;
  /**
   * Server label of the chat's license / work scope (e.g. `Quality Control`); null outside Work.
   */
  workContextLabel?: string | null;
}

export interface GetChatsQuery {
  context?: AiChatContext;
}

export interface GetChatsRes {
  items: ChatListItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface Chat extends Entity<string> {
  userEmail: string;
  title: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  messages: ChatMessage[];
  context?: AiChatContext;
  license?: string;
  /**
   * Work scope of a "Work" chat (e.g. `QUALITY_CONTROL`); at most one of `license` / `workScope` is
   * set.
   */
  workScope?: string | null;
  /**
   * Server label of the chat's license / work scope (e.g. `Quality Control`); null outside Work.
   */
  workContextLabel?: string | null;
}

/** Values for the ChatBot API `context` field. */
export enum AiChatContext {
  Personal = 'PERSONAL',
  Work = 'WORK',
}

/** Option of `WORK.workContexts` in GET /v1/chats/contexts; `group` is the discriminator. */
export type WorkContextOption = (
  | { group: 'OPERATIONAL'; workScope: string }
  | { group: 'SUPPORT'; license: string }
) & {
  /** Server label to show (e.g. `COM (Pin-up)`); the code is shown when absent. */
  label?: string;
};

/** Single item returned by GET /v1/chats/contexts. */
export interface ChatContextItem {
  id: string;
  role?: string;
  /** WORK only; absent when the user has no options — a Work chat is then created without one. */
  workContexts?: WorkContextOption[];
}

/** GET /v1/chats/contexts — plain array of context items. */
export type GetUserContextsRes = ChatContextItem[];

export enum ChatMessageReplyTypes {
  SIMPLE = 'SIMPLE',
  VACATION_BALANCE = 'VACATION_BALANCE',
  SICK_LEAVE_BALANCE = 'SICK_LEAVE_BALANCE',
  SICK_LEAVE_80_BALANCE = 'SICK_LEAVE_80_BALANCE',
  DAY_OFF_BALANCE = 'DAY_OFF_BALANCE',
  RECOVERY_BALANCE = 'RECOVERY_BALANCE',
  FAMILY_BALANCE = 'FAMILY_BALANCE',
  TOTAL_BALANCE = 'TOTAL_BALANCE',
  JIRA_TASK = 'JIRA_TASK',
  JIRA_READY_TO_CREATE = 'JIRA_READY_TO_CREATE',
}

export type ChatMessageTimeOfTypes = Exclude<
  ChatMessageReplyTypes,
  | ChatMessageReplyTypes.SIMPLE
  | ChatMessageReplyTypes.TOTAL_BALANCE
  | ChatMessageReplyTypes.JIRA_TASK
  | ChatMessageReplyTypes.JIRA_READY_TO_CREATE
>;

export enum ChatMessageFeedbackReasons {
  inappropriate = 'INAPPROPRIATE',
  wrong = 'WRONG',
  unacceptable = 'UNACCEPTABLE',
  other = 'OTHER',
}

export enum ChatMessageStatus {
  InProgress = 'IN_PROGRESS',
  Undelivered = 'UNDELIVERED',
  Delivered = 'DELIVERED',
  Error = 'ERROR',
}

export enum InternalMessageStatus {
  pending = 'pending',
  resolved = 'resolved',
  failed = 'failed',
  timeout = 'timeout',
}

export enum ChatMessageReaction {
  like = 'POSITIVE',
  dislike = 'NEGATIVE',
  none = null,
}

export interface ChatMessageFeedback {
  text: string;
  feedbackType: ChatMessageFeedbackReasons;
}

export interface ChatMessageTimeOffBalance {
  type: ChatMessageTimeOfTypes;
  value: number;
}

/**
 * View model for the bot bubble. Maps API `ChatMessage` (role/content) onto
 * the template fields (`reply`, optional time-off / PP metadata).
 */
export interface BotChatMessageView extends Entity<string> {
  chatId: string;
  request: string;
  reply: string;
  createdAt: string;
  lang: 'ru' | 'en' | 'uk';
  links: Nullable<Pick<ChatLink, 'label' | 'url'>[]>;
  internalStatus?: InternalMessageStatus;
  status?: ChatMessageStatus;
  isSuccessReply?: boolean;
  reactionType?: ChatMessageReaction;
  isFeedbackProvided?: boolean;
  ppSlackId?: string;
  ppFullName?: string;
  ppConfluencePage?: string;
  replyType: ChatMessageReplyTypes;
  timeOffBalances?: ChatMessageTimeOffBalance[];
  chatStarted?: string;
  useBlockMarkdown?: boolean;
}

/** Answer rating sent to the feedback endpoint. `null` clears the selection. */
export enum MessageRating {
  helpful = 'HELPFUL',
  notHelpful = 'NOT_HELPFUL',
}

/** Body for PUT /v1/chats/messages/{messageId}/feedback. */
export interface SubmitFeedbackReq {
  rating: MessageRating | null;
  reason?: ChatMessageFeedbackReasons;
  comment?: string;
}

/** Store input for toggling an answer's thumbs up/down selection. */
export interface ToggleReactionReq {
  id: string;
  reaction: ChatMessageReaction;
}

/** Store input for submitting the "Leave a review" form (always a thumbs-down answer). */
export interface LeaveFeedbackReq {
  id: string;
  reason: ChatMessageFeedbackReasons;
  comment: string;
}
